import { Component } from '@theme/component';

/**
 * Merges any currently active native `filter.*` query params (color, size,
 * price, etc. -- see assets/facets.js) into a metaobject-filter-generated
 * URL, so switching a department/facet tag never drops an active native
 * filter. `sort_by`/tag path segments already present on `urlOrPathname` win
 * over the live value for the same key.
 *
 * @param {string} urlOrPathname - A full href or a bare pathname
 * @returns {string} The merged URL as a string
 */
function mergeLiveFilterParams(urlOrPathname) {
  const url = new URL(urlOrPathname, window.location.origin);
  const liveParams = new URL(window.location.href).searchParams;

  for (const [key, value] of liveParams) {
    if (!key.startsWith('filter.')) continue;
    if (url.searchParams.has(key)) continue;
    url.searchParams.append(key, value);
  }

  return url.toString();
}

/**
 * The metaobject-driven Department drill-down filter (see
 * snippets/metaobject-department-filter.liquid). Intercepts link clicks and
 * peek-trigger toggles; navigates via the existing
 * facets-form-component's Section Rendering API path instead of a full page
 * reload.
 *
 * @extends {Component}
 */
class MetaobjectDepartmentComponent extends Component {
  /**
   * Navigates to a department/subcategory/type tag URL via the Section
   * Rendering API instead of a full page reload.
   * @param {MouseEvent} event
   */
  navigateTag(event) {
    const anchor = event.target;
    if (!(anchor instanceof HTMLAnchorElement)) return;

    event.preventDefault();

    const facetsForm = this.closest('facets-form-component');
    if (typeof (/** @type {any} */ (facetsForm)?.updateFiltersByURL) !== 'function') return;

    const url = mergeLiveFilterParams(anchor.href);
    /** @type {any} */ (facetsForm).updateFiltersByURL(url);
  }

  /**
   * Toggles a tag-less "peek" row's nested sublist open/closed, purely
   * client-side (no navigation, no product-tag filtering). Collapses every
   * other branch at the same nesting level first, including anything
   * expanded deeper within those branches, so switching from one peeked
   * branch to another never leaves a stale expanded row nested under an
   * unrelated, now-collapsed sibling.
   * @param {MouseEvent} event
   */
  togglePeek(event) {
    const trigger = event.target;
    if (!(trigger instanceof HTMLElement) || !trigger.matches('[data-peek-trigger]')) return;

    const item = trigger.closest('.metaobject-department-filter__item');
    if (!item) return;

    const sublist = item.querySelector(':scope > .metaobject-department-filter__sublist');
    const willExpand = !trigger.classList.contains('is-expanded');

    const siblingList = item.parentElement;
    if (siblingList) {
      for (const siblingItem of siblingList.children) {
        if (siblingItem === item) continue;
        this.#collapseWithin(siblingItem);
      }
    }

    trigger.classList.toggle('is-expanded', willExpand);
    trigger.setAttribute('aria-expanded', String(willExpand));
    sublist?.classList.toggle('is-expanded', willExpand);
  }

  /**
   * Collapses every expanded peek-trigger/sublist within (and including
   * descendants of) the given root.
   * @param {Element} root
   */
  #collapseWithin(root) {
    root.querySelectorAll('.metaobject-department-filter__peek-trigger.is-expanded').forEach((element) => {
      element.classList.remove('is-expanded');
      element.setAttribute('aria-expanded', 'false');
    });
    root.querySelectorAll('.metaobject-department-filter__sublist.is-expanded').forEach((element) => {
      element.classList.remove('is-expanded');
    });
  }
}

if (!customElements.get('metaobject-department-component')) {
  customElements.define('metaobject-department-component', MetaobjectDepartmentComponent);
}

/**
 * The metaobject-driven multi-select Facet filter (see
 * snippets/metaobject-facet-filter.liquid). Collects every checked tag
 * input across every group (plus preserved hidden tags for anything active
 * that isn't itself a facet option) and navigates via the same
 * facets-form-component Section Rendering API path as the department
 * filter.
 *
 * @extends {Component}
 */
class MetaobjectFacetsComponent extends Component {
  /**
   * Rebuilds the tag-path URL from every checked [data-metaobject-tag-input]
   * and updates the section.
   */
  toggleFacet() {
    const facetsForm = this.closest('facets-form-component');
    if (typeof (/** @type {any} */ (facetsForm)?.updateFiltersByURL) !== 'function') return;

    const baseUrl = this.dataset.baseUrl;
    if (!baseUrl) return;

    const tags = Array.from(this.querySelectorAll('[data-metaobject-tag-input]:checked'))
      .map((input) => (input instanceof HTMLInputElement ? input.value : ''))
      .filter(Boolean);

    const pathname = tags.length > 0 ? `${baseUrl}/${tags.join('+')}` : baseUrl;
    const url = mergeLiveFilterParams(pathname);

    /** @type {any} */ (facetsForm).updateFiltersByURL(url);
  }
}

if (!customElements.get('metaobject-facets-component')) {
  customElements.define('metaobject-facets-component', MetaobjectFacetsComponent);
}
